import { defineComponent as _defineComponent } from 'vue'
import { computed, onMounted, ref } from 'vue'

  import QuestionWithButtons from '@/components/common/QuestionWithButtons.vue'
  import Choice from '@/components/common/Choice.vue'
  import KetchUpButton from '@/components/common/KetchUpButton.vue'
  import SVGRenderer from '@/components/common/SVGRenderer.vue'

  import useIcons from '@/composables/useIcons'
  import useCourse from '@/composables/useCourse'
  import useCommonMixin from '@/composables/useCommonMixin'
  import useGamification from '@/composables/useGamification'
  import useAuthentication from '@/composables/useAuthentication'
  import useI18n from '@/composables/useI18n'

  import { UserModule } from '@/store/modules/user'
  import { CourseModule } from '@/store/modules/course'

  import CourseApi from '@/services/api/CourseApi'

  import type { Nullable, Question, QuestionAnswer } from '@/services/interfaces/QuizQuestion'
  import CookieHelper from '@/helpers/CookieHelper'
  import eventBus from '@/main'

  interface CourseSelection {
    id: string
    title: string
    area: string
    selected: boolean
  }

  interface AuthItem {
    id: string
    url: string
    description: string
    buttonText: string
  }

  
export default /*#__PURE__*/_defineComponent({
  __name: 'ChromeTabExtension',
  setup(__props) {

  const { checkboxSquareChecked, checkboxSquare, arrowRight, curvedDownArrow2 } = useIcons()
  const { fetchAllCourseModules, showStartCourseForFreeModal, courseSectionsAssignedCourses } = useCourse()
  const { convertNumberAnswer, hasValidUser } = useCommonMixin()
  const { trackGamificationActivity } = useGamification()
  const { tokenKey } = useAuthentication()
  const { translateString } = useI18n()

  const BASE_URL = window.origin
  const TAB_COURSES = 'tab_courses_'
  const courses = ref([
    {
      id: '63d7d5bb-e8e9-429d-b360-cefdecd80448',
      title: 'Vorbereitung auf die IHK-Sachkundeprüfung Immobiliardarlehensvermittlung - § 34i (schriftlich)',
      area: '$ 34i GewO',
      selected: false,
    },
    {
      id: '8de44221-a055-4cdb-8ce5-d7ac148cb49e',
      title: 'Vorbereitung auf die IHK-Sachkundeprüfung Versicherungsvermittlung - § 34d (schriftlich)',
      area: '$ 34d GewO',
      selected: false,
    },
    {
      id: '84be496a-b904-4184-b456-98736fea9259',
      title: 'Vorbereitung auf die IHK- Sachkundeprüfung Finanzanlagenvermittlung - § 34f (schriftlich)',
      area: '$ 34f GewO',
      selected: false,
    },
  ] as CourseSelection[])

  const authItems: AuthItem[] = [
    {
      id: 'login',
      url: '/login?redirect=/chrome-extension',
      description: translateString('browserExtension.loginDescription'),
      buttonText: translateString('browserExtension.loginButtonText'),
    },
    {
      id: 'register',
      url: 'https://sachkunde.ketchup.academy/registrierung',
      description: translateString('browserExtension.registerDescription'),
      buttonText: translateString('browserExtension.registerButtonText'),
    },
  ]

  const openPopup = (url: string): Window => {
    const width = 1200
    const height = 960
    const left = window.screenX + (window.innerWidth - width) / 2
    const top = window.screenY + (window.innerHeight - height) / 2

    return window.open(
      url,
      'ketchup:register:popup',
      `left=${left},top=${top},width=${width},height=${height},resizable,scrollbars=yes,status=1`,
    ) as Window
  }

  const onAuthButtonClick = (item: AuthItem) => {
    if (item.id === 'register') {
      const registerWindow = openPopup(item.url)
      window.addEventListener('message', (e: MessageEvent) => {
        if (e.origin === 'https://sachkunde.ketchup.academy') {
          CookieHelper.setCrossDomainCookie(tokenKey.value, e.data)
          registerWindow.close()
          window.location.reload()
        }
      })
      return
    }
    eventBus.$auth.loginWithPopup({
      redirect_uri: `${window.location.origin}/login/callback?redirect=/chrome-extension`,
    })
  }

  const isCourseTrialStarted = computed(
    () => (courseId: string) => !!courseSectionsAssignedCourses.value?.find((c) => c.id === courseId && c.trial),
  )

  const isPurchasedCourse = computed(
    () => (courseId?: string) => !!courseSectionsAssignedCourses.value?.find((c) => c.id === courseId && c.purchased),
  )

  if (hasValidUser.value) fetchAllCourseModules(courses.value.map((c) => c.id))

  const updateLocalStorageValue = () => {
    const userCourseIndices = courses.value.map((c) => c.selected)
    localStorage.setItem(TAB_COURSES + UserModule.id, JSON.stringify(userCourseIndices))
  }

  onMounted(() => {
    const userCourseIndices = localStorage.getItem(TAB_COURSES + UserModule.id)
    if (userCourseIndices) {
      courses.value.forEach((course, index) => {
        course.selected = JSON.parse(userCourseIndices)[index]
      })
    }
    if (hasValidUser.value) fetchNextQuestion()
  })

  const currentQuestionCourse = computed(() => {
    if (!question.value) return null
    return courses.value.find((course) => course.id === question.value!.courseMeta?.courseId)
  })

  const onCourseClick = (courseId: string) => {
    const selectedCourse = courses.value.find((course) => course.id === courseId) as CourseSelection
    if (!(isCourseTrialStarted.value(selectedCourse.id) || isPurchasedCourse.value(selectedCourse.id))) {
      showStartCourseForFreeModal({
        callback: () => {
          handleSelectedCourseCallback(selectedCourse)
        },
      })
      return
    }
    handleSelectedCourseCallback(selectedCourse)
  }

  const handleSelectedCourseCallback = async (selectedCourse: CourseSelection) => {
    selectedCourse.selected = !selectedCourse.selected
    updateLocalStorageValue()
    const selectedCourses = courses.value.filter((c) => c.selected).map((c) => c.id)
    if (!selectedCourses.length) {
      question.value = null
      return
    }
    if (question.value === null) {
      question.value = await CourseApi.getRandomQuestion([selectedCourse.id])
    }
  }

  const question = ref(null as Nullable<Question>)
  const inResultsMode = ref(false)
  const fetchNextQuestion = async (answers?: QuestionAnswer[]) => {
    if (inResultsMode.value || !answers) {
      const courseIds = courses.value.filter((course) => course.selected).map((course) => course.id)
      if (courseIds.length) {
        question.value = await CourseApi.getRandomQuestion(courseIds)
      }
      inResultsMode.value = false
      return
    }
    if (answers?.length && !inResultsMode.value) {
      await CourseApi.postRandomQuestionAnswer(
        question.value!.lesson!.id,
        answers[0].questionId,
        answers[0].selectedAnswerIds,
      )
      inResultsMode.value = true
      handleGamificationActivity(answers)
    }
  }

  const currentCorrectAnswers = computed(() => question.value?.answers.filter((ans) => ans.isCorrect) || [])

  const handleGamificationActivity = (answers: QuestionAnswer[]) => {
    let questionAnsweredCorrectly = false
    if (question.value?.questionType === 'number') {
      const numberAnswer = convertNumberAnswer(answers[0].selectedAnswerIds[0] || '')
      questionAnsweredCorrectly = numberAnswer === currentCorrectAnswers.value[0].answer
    } else if (
      currentCorrectAnswers.value?.length === answers[0].selectedAnswerIds.length &&
      currentCorrectAnswers.value?.every((answer) => answers[0].selectedAnswerIds.includes(answer.answerId))
    ) {
      questionAnsweredCorrectly = true
    }

    if (questionAnsweredCorrectly) {
      trackGamificationActivity('RANDOM_QUESTION_ANSWERED_CORRECTLY', {
        courseId: currentQuestionCourse.value?.id || '',
        moduleId: questionModule.value?.id || '',
        lessonId: question.value?.lesson?.id || null,
      })
    }
  }

  const questionModule = computed(() => {
    const courseModules = CourseModule.coursesModuleMap[currentQuestionCourse.value?.id || '']
    if (!courseModules) return
    return courseModules.find((m) => m.lessons.find((l) => l.id === question.value?.lesson?.id || ''))
  })

return { __sfc: true,checkboxSquareChecked, checkboxSquare, arrowRight, curvedDownArrow2, fetchAllCourseModules, showStartCourseForFreeModal, courseSectionsAssignedCourses, convertNumberAnswer, hasValidUser, trackGamificationActivity, tokenKey, translateString, BASE_URL, TAB_COURSES, courses, authItems, openPopup, onAuthButtonClick, isCourseTrialStarted, isPurchasedCourse, updateLocalStorageValue, currentQuestionCourse, onCourseClick, handleSelectedCourseCallback, question, inResultsMode, fetchNextQuestion, currentCorrectAnswers, handleGamificationActivity, questionModule, computed, onMounted, ref, QuestionWithButtons, Choice, KetchUpButton, SVGRenderer, useIcons, useCourse, useCommonMixin, useGamification, useAuthentication, useI18n, UserModule, CourseModule, CourseApi, CookieHelper, eventBus }
}

})